import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

type FireArgs = [SweetAlertOptions] | [string, string?, SweetAlertOptions['icon']?];

let configured = false;

const normalizeOptions = (args: FireArgs): SweetAlertOptions => {
  if (typeof args[0] === 'string') {
    return {
      title: args[0],
      text: typeof args[1] === 'string' ? args[1] : undefined,
      icon: args[2],
    };
  }

  return { ...(args[0] || {}) };
};

const mergeClasses = (base?: SweetAlertOptions['customClass'], extra?: SweetAlertOptions['customClass']) => ({
  ...(base || {}),
  ...(extra || {}),
});

const shouldRenderAsToast = (options: SweetAlertOptions) => {
  if (options.toast) return false;
  if (options.input) return false;
  if (options.showCancelButton) return false;
  if (options.showDenyButton) return false;
  if (options.footer) return false;
  if (typeof options.preConfirm === 'function') return false;
  if (options.allowOutsideClick === false) return false;
  return true;
};

export const setupSweetAlertDefaults = () => {
  if (configured) return;
  configured = true;

  const originalFire = Swal.fire.bind(Swal) as (...args: FireArgs) => Promise<SweetAlertResult<any>>;

  (Swal as any).fire = (...args: FireArgs) => {
    const options = normalizeOptions(args);

    if (shouldRenderAsToast(options)) {
      const existingDidOpen = options.didOpen;
      const existingWillClose = options.willClose;

      return originalFire({
        ...options,
        toast: true,
        position: options.position || 'top-end',
        showConfirmButton: false,
        timer: options.timer ?? 5200,
        timerProgressBar: options.timerProgressBar ?? true,
        backdrop: false,
        customClass: mergeClasses(options.customClass, {
          popup: `uzima-toast-popup ${options.customClass?.popup || ''}`.trim(),
          title: `uzima-toast-title ${options.customClass?.title || ''}`.trim(),
          htmlContainer: `uzima-toast-html ${options.customClass?.htmlContainer || ''}`.trim(),
          icon: `uzima-toast-icon ${options.customClass?.icon || ''}`.trim(),
        }),
        didOpen: (popup) => {
          popup.addEventListener('mouseenter', Swal.stopTimer);
          popup.addEventListener('mouseleave', Swal.resumeTimer);
          existingDidOpen?.(popup);
        },
        willClose: (popup) => {
          existingWillClose?.(popup);
        },
      });
    }

    return originalFire({
      ...options,
      customClass: mergeClasses(options.customClass, {
        popup: `uzima-modal-popup ${options.customClass?.popup || ''}`.trim(),
      }),
    });
  };
};

export const showToast = (options: SweetAlertOptions) =>
  Swal.fire({
    ...options,
    toast: true,
    position: options.position || 'top-end',
    showConfirmButton: false,
    timer: options.timer ?? 5200,
    timerProgressBar: options.timerProgressBar ?? true,
    backdrop: false,
    customClass: mergeClasses(options.customClass, {
      popup: `uzima-toast-popup ${options.customClass?.popup || ''}`.trim(),
      title: `uzima-toast-title ${options.customClass?.title || ''}`.trim(),
      htmlContainer: `uzima-toast-html ${options.customClass?.htmlContainer || ''}`.trim(),
      icon: `uzima-toast-icon ${options.customClass?.icon || ''}`.trim(),
    }),
  });

export const showConfirmDialog = (options: SweetAlertOptions) =>
  Swal.fire({
    ...options,
    customClass: mergeClasses(options.customClass, {
      popup: `uzima-modal-popup ${options.customClass?.popup || ''}`.trim(),
    }),
  });
